import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import styles from './CityNews.module.css';
import heroStyles from '../Components/Hero.module.css';
import { timeAgo } from '../Dashboard/timeUtils';
import { Search } from 'lucide-react';

const SheharPage = ({ lang = 'EN' }) => {
  const { categoryName } = useParams(); // URL se dynamic category uthayega
  const [sheharNews, setSheharNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [dateFilterType, setDateFilterType] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchSheharNews = async () => {
      setLoading(true);
      try {
        // Firebase query jo har mumkin variant ko check karegi
        const q = query(
          collection(db, "news"), 
          where("newsCategory", "in", [
            "Shehar", 
            "Shehar Ki News", 
            "city", 
            categoryName, 
            decodeURIComponent(categoryName || '')
          ])
        );
        const querySnapshot = await getDocs(q);
        let newsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        newsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setSheharNews(newsList);
        setFilteredNews(newsList);
      } catch (err) {
        console.error("Error fetching Shehar news:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSheharNews();
  }, [categoryName]);

  // Filtering Logic
  useEffect(() => {
    let result = sheharNews;

    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.titleEn && item.titleEn.toLowerCase().includes(term)) ||
        (item.titleUr && item.titleUr.toLowerCase().includes(term))
      );
    }

    const now = new Date();
    if (dateFilterType === 'today') {
      result = result.filter(item => {
        if (!item.timestamp) return false;
        const itemDate = new Date(item.timestamp);
        return itemDate.toDateString() === now.toDateString();
      });
    } else if (dateFilterType === 'week') {
      result = result.filter(item => {
        if (!item.timestamp) return false;
        const itemDate = new Date(item.timestamp);
        const diffTime = Math.abs(now - itemDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7;
      });
    }

    if (selectedYear !== 'All') {
      result = result.filter(item => {
        const dateStr = item.date || item.timestamp;
        if (!dateStr) return false;
        const itemYear = new Date(dateStr).getFullYear().toString();
        return itemYear === selectedYear;
      });
    }

    if (selectedMonth !== 'All') {
      result = result.filter(item => {
        const dateStr = item.date || item.timestamp;
        if (!dateStr) return false;
        const itemMonth = (new Date(dateStr).getMonth() + 1).toString();
        return itemMonth === selectedMonth;
      });
    }

    setFilteredNews(result);
  }, [searchTerm, selectedYear, selectedMonth, dateFilterType, sheharNews]);

  const years = ['All', '2026', '2025', '2024', '2023'];
  const months = [
    { label: 'All Months', value: 'All' },
    { label: 'January', value: '1' }, { label: 'February', value: '2' },
    { label: 'March', value: '3' }, { label: 'April', value: '4' },
    { label: 'May', value: '5' }, { label: 'June', value: '6' },
    { label: 'July', value: '7' }, { label: 'August', value: '8' },
    { label: 'September', value: '9' }, { label: 'October', value: '10' },
    { label: 'November', value: '11' }, { label: 'December', value: '12' },
  ];

  return (
    <div className={styles.container} dir={lang === 'UR' ? 'rtl' : 'ltr'}>
      
      <h2 className={styles.title}>
        {lang === 'EN' ? "City News (Shehar)" : "شہر کی خبریں"}
      </h2>

      {/* Search and Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} style={{ [lang === 'EN' ? 'left' : 'right']: '15px' }} />
          <input 
            type="text"
            placeholder={lang === 'EN' ? "Search City news by title..." : "شہر کی خبر عنوان سے تلاش کریں..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
            style={{ paddingLeft: lang === 'EN' ? '45px' : '15px', paddingRight: lang === 'UR' ? '45px' : '15px' }}
          />
        </div>

        <div className={styles.filtersRow}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{lang === 'EN' ? "Timeframe" : "وقت کا دورانیہ"}</label>
            <select value={dateFilterType} onChange={(e) => setDateFilterType(e.target.value)} className={styles.filterSelect}>
              <option value="all">{lang === 'EN' ? 'All Time' : 'تمام وقت'}</option>
              <option value="today">{lang === 'EN' ? 'Today' : 'آج کی خبریں'}</option>
              <option value="week">{lang === 'EN' ? 'Past 7 Days' : 'پچھلے 7 دن'}</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{lang === 'EN' ? "Month" : "مہینہ"}</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className={styles.filterSelect}>
              {months.map((m, idx) => (
                <option key={idx} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{lang === 'EN' ? "Year" : "سال"}</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className={styles.filterSelect}>
              {years.map((yr, idx) => (
                <option key={idx} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <h3 className={styles.statusMessage}>
          {lang === 'EN' ? "Loading City news..." : "شہر کی خبریں لوڈ ہو रही हैं..."}
        </h3>
      ) : filteredNews.length === 0 ? (
        <p className={styles.statusMessage}>
          {lang === 'EN' ? "No matching news found." : "آپ کی تلاش کے مطابق کوئی خبر نہیں ملی۔"}
        </p>
      ) : (
        <div className={styles.newsGrid}>
          {filteredNews.map(item => (
            <Link to={`/news/${item.id}`} key={item.id} className={styles.newsCard}>
              {item.randomHeroImage && (
                <img src={item.randomHeroImage} alt="news" className={styles.newsCardImg} />
              )}
              <div className={styles.newsCardBody}>
                {item.newsCategory && (
                  <span className={heroStyles.categoryBadge}>
                    {item.newsCategory}
                  </span>
                )}
                <h4 className={styles.newsCardTitle}>
                  {lang === 'EN' ? item.titleEn : item.titleUr}
                </h4>
                <div className={styles.newsCardMeta}>
                  {item.date && <span>{item.date} • </span>}
                  <span>{timeAgo(item.timestamp)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SheharPage;